import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown, Form } from 'react-bootstrap';
import CountUp from 'react-countup';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getOrders, updateOrder, deleteOrder, updateDeliveryStatus } from '../../../services/OrdersService';
import { getCurrentLanguage } from '../../../utils/i18nHelpers';
import Swal from 'sweetalert2';
import { DELIVERY_STATUS_OPTIONS, DELIVERY_STATUS_EMAIL_ELIGIBLE, getDeliveryStatusBadgeClass } from '../Orders/deliveryStatusConstants';
import DeliveryStatusEmailModal from '../Orders/DeliveryStatusEmailModal';

function formatDate(d) {
    if (!d) return '-';
    const locale = getCurrentLanguage() === 'nl' ? 'nl-NL' : 'en-GB';
    return new Date(d).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function TasksPage() {
    const { t } = useTranslation();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20); // default 20; options: 50, 100
    const [filterOrderNumber, setFilterOrderNumber] = useState('');
    const [filterOrderName, setFilterOrderName] = useState('');
    const [filterBuyer, setFilterBuyer] = useState('');
    const [filterSource, setFilterSource] = useState('all');
    const [filterReview, setFilterReview] = useState(false);
    const [filterMarketing, setFilterMarketing] = useState(false);
    const [sortBy, setSortBy] = useState(null); // 'order_date' | 'delivery_date' | 'total' | null
    const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
    const [deliveryModal, setDeliveryModal] = useState(null); // { orderId, orderNumber, targetStatus } | null
    const [deliverySubmitting, setDeliverySubmitting] = useState(false);

    const load = async () => {
        try {
            setLoadError(null);
            setLoading(true);
            const res = await getOrders();
            setOrders(res.data || []);
        } catch (err) {
            const status = err.response?.status;
            const msg = err.response?.data?.error || err.message || t('tasksPage.errors.loadOrders');
            setLoadError({ message: msg, status });
            if (err.code === 'ERR_NETWORK' || err.message?.includes('Network')) {
                toast.error(t('tasksPage.errors.networkError'));
            } else if (status === 502) {
                toast.error(t('tasksPage.errors.serverError502'));
            } else {
                toast.error(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filteredOrders = useMemo(() => {
        let data = orders.filter((o) => {
            const orderNum = String(o.order_number || '').toLowerCase();
            const name = String((o.notes || '') + ' Order #' + (o.order_number || '')).toLowerCase();
            const buyer = String(o.buyer_name || '').toLowerCase();
            const fNum = filterOrderNumber.trim().toLowerCase();
            const fName = filterOrderName.trim().toLowerCase();
            const fBuyer = filterBuyer.trim().toLowerCase();
            if (fNum && !orderNum.includes(fNum)) return false;
            if (fName && !name.includes(fName)) return false;
            if (fBuyer && !buyer.includes(fBuyer)) return false;
            if (filterSource !== 'all' && (o.source || 'manual') !== filterSource) return false;
            if (filterReview && !o.needs_review) return false;
            if (filterMarketing && !o.is_marketing) return false;
            return true;
        });
        if (sortBy) {
            const mult = sortDir === 'asc' ? 1 : -1;
            data = [...data].sort((a, b) => {
                if (sortBy === 'order_date') {
                    const da = new Date(a.order_date || a.created_at || 0).getTime();
                    const db = new Date(b.order_date || b.created_at || 0).getTime();
                    return mult * (da - db);
                }
                if (sortBy === 'delivery_date') {
                    const da = new Date(a.delivery_date || a.order_date || a.created_at || 0).getTime();
                    const db = new Date(b.delivery_date || b.order_date || b.created_at || 0).getTime();
                    return mult * (da - db);
                }
                const va = parseFloat(a.total) || 0;
                const vb = parseFloat(b.total) || 0;
                return mult * (va - vb);
            });
        }
        return data;
    }, [orders, filterOrderNumber, filterOrderName, filterBuyer, filterSource, filterReview, filterMarketing, sortBy, sortDir]);

    // If filters/page size change, jump back to first page
    useEffect(() => {
        setCurrentPage(1);
    }, [filterOrderNumber, filterOrderName, filterBuyer, filterSource, filterReview, filterMarketing, pageSize]);

    const handleSort = (col) => {
        if (sortBy === col) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortDir('desc');
        }
        setCurrentPage(1);
    };

    const SortableTh = ({ col, label }) => {
        const arrow = sortBy === col ? (sortDir === 'asc' ? '↑' : '↓') : '↓';
        return (
            <th
                role="button"
                className="user-select-none"
                onClick={() => handleSort(col)}
                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
                {label}
                <span
                    className="ms-2"
                    style={{
                        fontSize: '1.2rem',
                        fontWeight: sortBy === col ? 'bold' : 'normal',
                        color: sortBy === col ? '#0d6efd' : '#6c757d',
                        opacity: sortBy === col ? 1 : 0.7,
                    }}
                    aria-hidden
                >
                    {arrow}
                </span>
            </th>
        );
    };

    const deliveryStatusCounts = DELIVERY_STATUS_OPTIONS.reduce((acc, s) => {
        acc[s.value] = filteredOrders.filter(o => (o.delivery_status || 'preparing') === s.value).length;
        return acc;
    }, {});

    const lastIndex = currentPage * pageSize;
    const firstIndex = lastIndex - pageSize;
    const records = filteredOrders.slice(firstIndex, lastIndex);
    const npage = Math.ceil(filteredOrders.length / pageSize) || 1;
    const pageNumbers = [...Array(npage + 1).keys()].slice(1);
    const showingFrom = filteredOrders.length === 0 ? 0 : firstIndex + 1;
    const showingTo = filteredOrders.length === 0 ? 0 : Math.min(lastIndex, filteredOrders.length);

    const applyDeliveryStatus = async (orderId, newStatus, sendEmail, expectedDeliveryDate, deliveryWindow) => {
        setDeliverySubmitting(true);
        try {
            // The invoice PDF attached to a manual status email is made by the SERVER (print layout): nothing is uploaded from the browser.
            const payload = { delivery_status: newStatus, send_email: sendEmail };
            if (expectedDeliveryDate) payload.expected_delivery_date = expectedDeliveryDate;
            if (expectedDeliveryDate && deliveryWindow) payload.delivery_window = deliveryWindow;
            const res = await updateDeliveryStatus(orderId, payload);
            const emailOutcome = res.data?.email;
            if (emailOutcome?.attempted && emailOutcome.sent === false) {
                toast.warning(t('tasksPage.toasts.deliveryStatusUpdatedEmailFailed', { error: emailOutcome.error || '' }));
            } else if (emailOutcome?.attempted && emailOutcome.sent === true) {
                toast.success(t('tasksPage.toasts.deliveryStatusUpdatedEmailSent'));
            } else {
                toast.success(t('tasksPage.toasts.deliveryStatusUpdated'));
            }
            setDeliveryModal(null);
            load();
        } catch (err) {
            const msg = err.response?.data?.error || t('tasksPage.errors.updateDeliveryStatus');
            toast.error(msg);
        } finally {
            setDeliverySubmitting(false);
        }
    };

    const handleDeliveryStatusChange = (orderId, orderNumber, newStatus) => {
        if (DELIVERY_STATUS_EMAIL_ELIGIBLE.includes(newStatus)) {
            setDeliveryModal({ orderId, orderNumber, targetStatus: newStatus });
        } else {
            applyDeliveryStatus(orderId, newStatus, false);
        }
    };

    const handlePaymentStatusChange = async (orderId, newPaymentStatus) => {
        try {
            await updateOrder(orderId, { payment_status: newPaymentStatus });
            toast.success(t('tasksPage.toasts.paymentStatusUpdated'));
            load();
        } catch {
            toast.error(t('tasksPage.errors.updatePaymentStatus'));
        }
    };

    const handleAmountPaidChange = async (orderId, newAmountPaid, orderTotal) => {
        try {
            // Validate amount
            const amount = parseFloat(newAmountPaid);
            if (isNaN(amount) || amount < 0) {
                toast.error(t('tasksPage.errors.invalidAmount'));
                return;
            }
            if (amount > orderTotal) {
                toast.error(t('tasksPage.errors.amountExceedsTotal', { total: orderTotal.toFixed(2) }));
                return;
            }

            await updateOrder(orderId, {
                payment_status: 'partial',
                amount_paid: amount
            });
            toast.success(t('tasksPage.toasts.amountPaidUpdated'));
            load();
        } catch (err) {
            const msg = err.response?.data?.error || err.message || t('tasksPage.errors.updateAmountPaid');
            toast.error(msg);
        }
    };

    const handleCancel = async (order) => {
        const result = await Swal.fire({
            title: t('tasksPage.dialogs.cancelTitle'),
            text: t('tasksPage.dialogs.cancelText', { number: order.order_number }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('tasksPage.dialogs.cancelConfirm'),
        });
        if (result.isConfirmed) {
            try {
                await updateDeliveryStatus(order.id, { delivery_status: 'cancelled', send_email: false });
                toast.success(t('tasksPage.toasts.orderCancelled'));
                load();
            } catch {
                toast.error(t('tasksPage.errors.cancelOrder'));
            }
        }
    };

    const handleDelete = async (order) => {
        const result = await Swal.fire({
            title: t('tasksPage.dialogs.deleteTitle'),
            text: t('tasksPage.dialogs.deleteText', { number: order.order_number }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('tasksPage.dialogs.deleteConfirm'),
        });
        if (result.isConfirmed) {
            try {
                await deleteOrder(order.id);
                toast.success(t('tasksPage.toasts.orderDeleted'));
                load();
            } catch (err) {
                const errorKey = err.response?.data?.error_key;
                const errorMsg = (errorKey && t(errorKey)) || err.response?.data?.error || err.message || t('tasksPage.errors.deleteOrder');
                toast.error(errorMsg);
            }
        }
    };

    const csvHeaders = [
        { label: t('tasksPage.csv.number'), key: 'order_number' },
        { label: t('tasksPage.csv.order'), key: 'order_number' },
        { label: t('tasksPage.csv.deliveryStatus'), key: 'delivery_status' },
        { label: t('tasksPage.csv.total'), key: 'total' },
        { label: t('tasksPage.csv.orderDate'), key: 'order_date' },
        { label: t('tasksPage.csv.deliveryDate'), key: 'delivery_date' },
        { label: t('tasksPage.csv.paid'), key: 'paid' },
        { label: t('tasksPage.csv.amountPaid'), key: 'amount_paid' },
        { label: t('tasksPage.csv.remaining'), key: 'amount_remaining' },
        { label: t('tasksPage.csv.notes'), key: 'notes' },
    ];

    const csvData = filteredOrders.filter(o => !o.is_test && !o.is_marketing).map(o => ({
        order_number: o.order_number,
        delivery_status: t(`deliveryStatuses.${o.delivery_status || 'preparing'}`),
        total: o.total,
        order_date: formatDate(o.order_date || o.created_at),
        delivery_date: formatDate(o.delivery_date || o.order_date || o.created_at),
        paid: o.payment_status === 'paid' ? t('common.yes') : o.payment_status === 'partial' ? t('tasksPage.paymentPartial') : o.payment_status === 'unpaid' ? t('common.no') : '—',
        amount_paid: o.amount_paid ?? 0,
        amount_remaining: o.amount_remaining ?? 0,
        notes: o.notes || '',
    }));

    return (
        <div className="container-fluid">
            {loadError && (
                <div className="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3" role="alert">
                    <span>
                        <strong>{t('tasksPage.loadError.title')}</strong>
                        {loadError.status === 502 && t('tasksPage.loadError.badGateway')}
                        {loadError.status !== 502 && `: ${loadError.message}`}
                        {t('tasksPage.loadError.suffix')}
                    </span>
                    <Button variant="outline-danger" size="sm" onClick={() => load()}>
                        {t('tasksPage.loadError.retry')}
                    </Button>
                </div>
            )}
            <div className="row">
                <div className="col-xl-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="row task">
                                {DELIVERY_STATUS_OPTIONS.map((item) => (
                                    <div className="col-xl-2 col-sm-4 col-6" key={item.value}>
                                        <div className="task-summary">
                                            <div className="d-flex align-items-baseline">
                                                <CountUp
                                                    className={`mb-0 fs-28 fw-bold me-2 text-${item.badgeClass}`}
                                                    end={deliveryStatusCounts[item.value] || 0}
                                                    duration={0.5}
                                                />
                                                <h6 className="mb-0">{t(`deliveryStatuses.${item.value}`)}</h6>
                                            </div>
                                            <p>{t('tasksPage.ordersLabel')}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-xl-12">
                    <div className="card">
                        <div className="card-body p-0">
                            <div className="table-responsive active-projects task-table">
                                <div className="tbl-caption d-flex justify-content-between align-items-center">
                                    <h4 className="heading mb-0">{t('tasksPage.ordersLabel')}</h4>
                                    <div>
                                        <Link to="/orders/create" className="btn btn-primary btn-sm me-2">
                                            <i className="fa-solid fa-plus me-1" /> {t('tasksPage.newOrder')}
                                        </Link>
                                        <CSVLink
                                            headers={csvHeaders}
                                            data={csvData}
                                            filename="tabliya-orders.csv"
                                            className="btn btn-primary light btn-sm"
                                        >
                                            <i className="fa-solid fa-file-excel" /> {t('tasksPage.exportReport')}
                                        </CSVLink>
                                    </div>
                                </div>
                                <div id="task-tbl_wrapper" className="dataTables_wrapper no-footer">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status" />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="d-flex gap-2 flex-wrap mb-2 pb-2 border-bottom">
                                                <Form.Control
                                                    size="sm"
                                                    placeholder={t('tasksPage.filterOrderNumber')}
                                                    value={filterOrderNumber}
                                                    onChange={(e) => { setFilterOrderNumber(e.target.value); setCurrentPage(1); }}
                                                    style={{ maxWidth: 140 }}
                                                />
                                                <Form.Control
                                                    size="sm"
                                                    placeholder={t('tasksPage.filterName')}
                                                    value={filterOrderName}
                                                    onChange={(e) => { setFilterOrderName(e.target.value); setCurrentPage(1); }}
                                                    style={{ maxWidth: 140 }}
                                                />
                                                <Form.Control
                                                    size="sm"
                                                    placeholder={t('tasksPage.filterBuyer')}
                                                    value={filterBuyer}
                                                    onChange={(e) => { setFilterBuyer(e.target.value); setCurrentPage(1); }}
                                                    style={{ maxWidth: 140 }}
                                                />
                                                <Form.Select
                                                    size="sm"
                                                    aria-label={t('orders.filters.allSources')}
                                                    value={filterSource}
                                                    onChange={(e) => { setFilterSource(e.target.value); setCurrentPage(1); }}
                                                    style={{ maxWidth: 160 }}
                                                >
                                                    <option value="all">{t('orders.filters.allSources')}</option>
                                                    {['shopify', 'website', 'manual'].map((src) => (
                                                        <option key={src} value={src}>{t(`orders.flags.source.${src}`)}</option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Check
                                                    type="switch"
                                                    id="filter-needs-review"
                                                    className="align-self-center"
                                                    label={t('orders.filters.needsReview')}
                                                    checked={filterReview}
                                                    onChange={(e) => { setFilterReview(e.target.checked); setCurrentPage(1); }}
                                                />
                                                <Form.Check
                                                    type="switch"
                                                    id="filter-marketing"
                                                    className="align-self-center"
                                                    label={t('orders.filters.marketing')}
                                                    checked={filterMarketing}
                                                    onChange={(e) => { setFilterMarketing(e.target.checked); setCurrentPage(1); }}
                                                />
                                            </div>
                                            <table id="task-tbl" className="table ItemsCheckboxSec dataTable no-footer mb-2 mb-sm-0">
                                                <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>{t('tasksPage.columns.name')}</th>
                                                        <th>{t('tasksPage.columns.deliveryStatus')}</th>
                                                        <SortableTh col="order_date" label={t('tasksPage.columns.orderDate')} />
                                                        <SortableTh col="delivery_date" label={t('tasksPage.columns.deliveryDate')} />
                                                        <SortableTh col="total" label={t('tasksPage.columns.total')} />
                                                        <th>{t('tasksPage.columns.buyer')}</th>
                                                        <th>{t('tasksPage.columns.paid')}</th>
                                                        <th>{t('tasksPage.columns.amountPaid')}</th>
                                                        <th>{t('tasksPage.columns.remaining')}</th>
                                                        <th>{t('tasksPage.columns.notes')}</th>
                                                        <th className="text-end">{t('tasksPage.columns.actions')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {records.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="12" className="text-center py-5 text-muted">
                                                                {orders.length === 0 ? t('tasksPage.noOrdersYet') : t('tasksPage.noMatchingOrders')}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        records.map((item, index) => (
                                                            <tr key={item.id}>
                                                                <td><span>{firstIndex + index + 1}</span></td>
                                                                <td>
                                                                    <Link to={`/orders/${item.id}`} className="text-decoration-none text-dark d-block" style={{ cursor: 'pointer' }}>
                                                                        <div className="products">
                                                                            <div>
                                                                                <h6 className="mb-0">
                                                                                    {t('tasksPage.orderNumber', { number: item.order_number })}
                                                                                    {item.source && item.source !== 'manual' && (
                                                                                        <span className="badge order-flag-badge order-flag-badge--source">{t(`orders.flags.source.${item.source}`)}</span>
                                                                                    )}
                                                                                    {item.is_test && <span className="badge order-flag-badge order-flag-badge--test">{t('orders.flags.test')}</span>}
                                                                                    {item.is_marketing && <span className="badge order-flag-badge order-flag-badge--marketing" data-testid="marketing-badge">{t('orders.flags.marketing')}</span>}
                                                                                    {item.needs_review && (
                                                                                        <span className="badge order-flag-badge order-flag-badge--review" title={item.review_reason || ''}>{item.deleted_in_shopify ? t('orders.flags.deletedInShopify') : t('orders.flags.needsReview')}</span>
                                                                                    )}
                                                                                </h6>
                                                                                <span className="text-muted small">
                                                                                    {(item.invoices && item.invoices.length > 0)
                                                                                        ? item.invoices.filter((inv) => inv.kind === 'invoice' || inv.kind === 'marketing').map((inv) => inv.invoice_number).join(', ')
                                                                                        : (item.external_name || '')}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </Link>
                                                                </td>
                                                                <td>
                                                                    <Dropdown className="task-dropdown-2">
                                                                        <Dropdown.Toggle
                                                                            as="div"
                                                                            className={`badge light border-0 badge-${getDeliveryStatusBadgeClass(item.delivery_status)}`}
                                                                        >
                                                                            {t(`deliveryStatuses.${item.delivery_status || 'preparing'}`)}
                                                                        </Dropdown.Toggle>
                                                                        <Dropdown.Menu className="task-drop-menu">
                                                                            {DELIVERY_STATUS_OPTIONS.map((s) => (
                                                                                <Dropdown.Item
                                                                                    key={s.value}
                                                                                    onClick={() => handleDeliveryStatusChange(item.id, item.order_number, s.value)}
                                                                                >
                                                                                    {t(`deliveryStatuses.${s.value}`)}
                                                                                </Dropdown.Item>
                                                                            ))}
                                                                        </Dropdown.Menu>
                                                                    </Dropdown>
                                                                </td>
                                                                <td><span>{formatDate(item.order_date || item.created_at)}</span></td>
                                                                <td><span>{formatDate(item.delivery_date || item.order_date || item.created_at)}</span></td>
                                                                <td><span>{item.total?.toFixed(2)}</span></td>
                                                                <td><span>{item.buyer_name || '-'}</span></td>
                                                                <td>
                                                                    <Dropdown className="task-dropdown-2">
                                                                        <Dropdown.Toggle
                                                                            as="div"
                                                                            className={`badge light border-0 ${
                                                                                item.payment_status === 'paid' ? 'badge-success' :
                                                                                item.payment_status === 'partial' ? 'badge-warning' : 'badge-secondary'
                                                                            }`}
                                                                            style={{ cursor: 'pointer' }}
                                                                        >
                                                                            {item.payment_status === 'paid' ? t('common.yes') : item.payment_status === 'partial' ? t('tasksPage.paymentPartial') : item.payment_status === 'unpaid' ? t('common.no') : '—'}
                                                                        </Dropdown.Toggle>
                                                                        <Dropdown.Menu className="task-drop-menu">
                                                                            <Dropdown.Item onClick={() => handlePaymentStatusChange(item.id, 'paid')}>
                                                                                {t('common.yes')}
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Item onClick={() => handlePaymentStatusChange(item.id, 'unpaid')}>
                                                                                {t('common.no')}
                                                                            </Dropdown.Item>
                                                                            <Dropdown.Item onClick={() => handlePaymentStatusChange(item.id, 'partial')}>
                                                                                {t('tasksPage.paymentPartial')}
                                                                            </Dropdown.Item>
                                                                        </Dropdown.Menu>
                                                                    </Dropdown>
                                                                </td>
                                                                <td>
                                                                    {item.payment_status === 'partial' ? (
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            max={item.total || 0}
                                                                            value={item.amount_paid ?? 0}
                                                                            onChange={(e) => {
                                                                                const newAmount = parseFloat(e.target.value) || 0;
                                                                                // Update local state immediately for better UX
                                                                                setOrders(prevOrders => 
                                                                                    prevOrders.map(o => 
                                                                                        o.id === item.id 
                                                                                            ? { 
                                                                                                ...o, 
                                                                                                amount_paid: newAmount,
                                                                                                amount_remaining: (o.total || 0) - newAmount
                                                                                            }
                                                                                            : o
                                                                                    )
                                                                                );
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                const newAmount = parseFloat(e.target.value) || 0;
                                                                                if (newAmount !== (item.amount_paid ?? 0)) {
                                                                                    handleAmountPaidChange(item.id, newAmount, item.total || 0);
                                                                                }
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    e.target.blur();
                                                                                }
                                                                            }}
                                                                            size="sm"
                                                                            style={{ width: '100px', display: 'inline-block' }}
                                                                        />
                                                                    ) : (
                                                                        <span>€{(item.amount_paid ?? 0).toFixed(2)}</span>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span>€{((item.total || 0) - (item.amount_paid ?? 0)).toFixed(2)}</span>
                                                                </td>
                                                                <td><span className="text-muted small" title={item.notes || ''}>{item.notes ? (item.notes.length > 50 ? item.notes.slice(0, 50) + '…' : item.notes) : '-'}</span></td>
                                                                <td className="text-end">
                                                                    <Link
                                                                        to={`/orders/${item.id}`}
                                                                        state={{ startEditing: true }}
                                                                        className="btn btn-sm btn-primary light me-1"
                                                                        title={t('tasksPage.actionsTitles.editOrder')}
                                                                    >
                                                                        <i className="fa-solid fa-pen" />
                                                                    </Link>
                                                                    <Link
                                                                        to={`/orders/${item.id}`}
                                                                        className="btn btn-sm btn-secondary light me-1"
                                                                        title={t('tasksPage.actionsTitles.invoice')}
                                                                    >
                                                                        <i className="fa-solid fa-file-invoice" />
                                                                    </Link>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-warning light me-1"
                                                                        title={t('tasksPage.actionsTitles.cancel')}
                                                                        onClick={() => handleCancel(item)}
                                                                    >
                                                                        <i className="fa-solid fa-ban" />
                                                                    </button>
                                                                    {item.is_test ? (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger light"
                                                                            title={t('tasksPage.actionsTitles.delete')}
                                                                            onClick={() => handleDelete(item)}
                                                                        >
                                                                            <i className="fa-solid fa-trash" />
                                                                        </button>
                                                                    ) : (
                                                                        <span className="d-inline-block" title={t('orders.deleteRealTip')} data-testid="delete-real-tip">
                                                                            <button type="button" className="btn btn-sm btn-danger light" disabled>
                                                                                <i className="fa-solid fa-trash" />
                                                                            </button>
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                            {orders.length > 0 && (
                                                <div className="d-sm-flex text-center justify-content-between align-items-center">
                                                    <div className="dataTables_info d-flex flex-wrap align-items-center gap-3 justify-content-center justify-content-sm-start">
                                                        <div className="d-inline-flex align-items-center gap-2">
                                                            <span>{t('tasksPage.show')}</span>
                                                            <Form.Select
                                                                size="sm"
                                                                value={pageSize}
                                                                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                                                style={{ width: 90 }}
                                                            >
                                                                <option value={20}>20</option>
                                                                <option value={50}>50</option>
                                                                <option value={100}>100</option>
                                                            </Form.Select>
                                                            <span>{t('tasksPage.entries')}</span>
                                                        </div>
                                                        <div>
                                                            {t('tasksPage.showingEntries', { from: showingFrom, to: showingTo, total: filteredOrders.length })}
                                                        </div>
                                                    </div>
                                                    <div className="dataTables_paginate paging_simple_numbers justify-content-center">
                                                        <Link
                                                            className={`paginate_button previous ${currentPage === 1 ? 'disabled' : ''}`}
                                                            to="#"
                                                            onClick={(e) => { e.preventDefault(); if (currentPage > 1) setCurrentPage(currentPage - 1); }}
                                                        >
                                                            <i className="fa-solid fa-angle-left" />
                                                        </Link>
                                                        <span>
                                                            {pageNumbers.map((n) => (
                                                                <Link
                                                                    key={n}
                                                                    className={`paginate_button ${currentPage === n ? 'current' : ''}`}
                                                                    to="#"
                                                                    onClick={(e) => { e.preventDefault(); setCurrentPage(n); }}
                                                                >
                                                                    {n}
                                                                </Link>
                                                            ))}
                                                        </span>
                                                        <Link
                                                            className={`paginate_button next ${currentPage === npage ? 'disabled' : ''}`}
                                                            to="#"
                                                            onClick={(e) => { e.preventDefault(); if (currentPage < npage) setCurrentPage(currentPage + 1); }}
                                                        >
                                                            <i className="fa-solid fa-angle-right" />
                                                        </Link>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <DeliveryStatusEmailModal
                show={!!deliveryModal}
                orderNumber={deliveryModal?.orderNumber}
                targetStatus={deliveryModal?.targetStatus}
                submitting={deliverySubmitting}
                onCancel={() => setDeliveryModal(null)}
                onConfirm={(sendEmail, expectedDeliveryDate, deliveryWindow) => applyDeliveryStatus(deliveryModal.orderId, deliveryModal.targetStatus, sendEmail, expectedDeliveryDate, deliveryWindow)}
            />
        </div>
    );
}
